import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpResponse, HttpErrorResponse, HttpInterceptor } from '@angular/common/http';
import { Observable, throwError, Subject } from 'rxjs';
import { catchError, map, take, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import { toCustomCamelCaseKey } from 'src/app/shared/util/util';
import AppResponseError from 'src/app/shared/models/app-response-error';
import { LoaderService } from 'src/app/loader.service';
import { TokenLogin } from 'src/app/shared/models/login-response';

@Injectable()
export class AppHttpInterceptor implements HttpInterceptor {

  /**
   * Variável  para ter o controle do número de requests em andamento.
   */
  private requests: HttpRequest<any>[];

  /**
   * Flag para evitar renovar o refreshToken mais de uma vez
   */
  private refreshTokenInProgress = false;

  /**
   * "Semáfaro" para auxiliar o processo de refreshToken
   */
  private refreshTokenSubject: Subject<any> =
    new Subject<any>();

  constructor(
    private authService: AuthService,
    private router: Router,
    private loaderService: LoaderService) {
    this.requests = new Array<HttpRequest<any>>();
  }

  /**
   *  Verifica se o response (em caso de erro) possuí descrição e título
   * @param info Response body.
   */
  private isNormalizedResponse(info: AppResponseError): boolean {
    if (!info || info.titulo === undefined || info.descricao === undefined) {
      return false;
    }
    return true;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    let r: HttpRequest<any> = req;


    if (!this.shouldIgnoreRequestForLoadingSpinner(req)) {
      this.loaderService.loading.next(true);
      this.requests.push(req);
      // console.log('Nº of requests ---> ' + this.requests.length + '+++ ' + req.url);
    }

    if (this.authService.token) {
      r = req.clone({
        // set additional header before send the request to the server
        headers: req.headers.set('Authorization', `Bearer ${this.authService.token}`),
      });
    }

    let requestObs$: Observable<HttpEvent<any>>;

    requestObs$ = (this.refreshTokenInProgress && !this.urlUsedForAuthentication(r.url))
      ? this.refreshTokenSubject.pipe(
        take(1),
        switchMap(newAccestoken => {
          const requestWithNewToken = req.clone({
            // Definindo novo accessToken para evitar 401 descenessários.
            headers: req.headers.set('Authorization', `Bearer ${this.authService.token}`),
          });
          return next.handle(requestWithNewToken)
        })
      )
      : next.handle(r);

    return requestObs$.pipe(
      catchError((err: HttpErrorResponse) => {
        let error: AppResponseError;

        // Tratando API Fora do ar:
        if (err.message.includes('Http failure response for') && err.statusText === 'Unknown Error' && err.status === 0) {
          error = {
            statusCode: 500,
            descricao: 'Não foi possível conectar ao servidor no momento',
            titulo: 'Erro!'
          } as AppResponseError;

          this.removeRequest(req);
          return throwError(error);
        }

        const errAux = toCustomCamelCaseKey(err);
        const info = errAux.error || {};


        if (err.status === 401
          && !this.urlUsedForAuthentication(req.url)) {
          const obs = this.handleRefreshProcess(req, next);
          return obs;
        }
        else if (err.status === 401) {

          if(!this.urlUsedForMultifator(req.url)){
            this.authService.clearStorage();
            this.router.navigate(['/', 'logon']);
          }
          
          error = {
            statusCode: err.status,
            descricao: info.descricao || info.Descricao || 'Credenciais expiradas ou serviço não disponível no momento',
            detalhe: info.detalhe || info.Detalhe,
            identificador: info.identificador || info.Identificador,
            titulo: info.titulo || info.Titulo || 'Erro!'
          } as AppResponseError;
        } else if (this.isNormalizedResponse(info)) {
          // Em caso de recebimento de uma resposta normalizada que não é 401
          error = {
            statusCode: err.status,
            descricao: info.descricao || info.Descricao,
            detalhe: info.detalhe || info.Detalhe,
            identificador: info.identificador || info.Identificador,
            titulo: info.titulo || info.Titulo
          } as AppResponseError;

        } else {
          // Servidor enviou uma resposta não normalizada.
          error = {
            statusCode: err.status,
            descricao: 'Contate o administrador',
            titulo: 'Um Erro Ocorreu',
            detalhe: err,
          } as AppResponseError;
          /*
          O trecho abaixo está comentado pois ao injetar apiService
          da ruim..
 
          A meta é que no futuro uma boa alma veja o motivo e solucione para a gente.
 
          // Logando apenas caso o problema não seja relacionado ao log
          if (!r.url.includes('/log/')) {
            this.apiService.loggarErro(
              // { sessao: 'app-http-interceptor -> url não normalizada', logLevel: 'fatal', conteudo: r }
              {
                sessao: 'HabJTxm0STMIWgsHXGOZYPR2WQAjNNJ0+P8acOSci5362zOJC22EmL+ehjCINFK4',
                logLevel: 'oY6Oy1BSdbOMcUApDlDJ6Q==', conteudo: r, criptografado: false
              }
            ).subscribe();
          }
          */
        }


        this.removeRequest(req);

        return throwError(error);

      }),
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          // convert body data to camelCase
          const responseBody = toCustomCamelCaseKey(event.body);
          this.removeRequest(req);

          return new HttpResponse({
            body: responseBody,
            headers: event.headers,
            status: event.status,
            statusText: event.statusText,
            url: event.url
          });
        }

        return event;
      }));
  }



  private shouldIgnoreRequestForLoadingSpinner(req: HttpRequest<any>): boolean {
    return req.url.includes("notification") || req.url.includes("api/conta/refresh");
  }

  private urlUsedForAuthentication(url: string): boolean {
    return url.endsWith("login") || url.endsWith("refresh") || url.endsWith("login-admin") || url.endsWith("verificar-multifator");
  }

  private urlUsedForMultifator(url: string): boolean {
    return url.endsWith("verificar-multifator");
  }

  /**
   * Utilizado para refletir naquele spinner
   */
  removeRequest(req: HttpRequest<any>) {

    if (this.shouldIgnoreRequestForLoadingSpinner(req))
      return;

    const reqIndex = this.requests.indexOf(req);
    if (reqIndex >= 0) {
      this.requests.splice(reqIndex, 1);
      // console.log('Nº of requests ---> ' + this.requests.length + '--- ' + req.url);
    }

    this.loaderService.loading.next(false);
  }

  private handleRefreshProcess(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let obs: Observable<HttpEvent<any>>;
    if (!this.refreshTokenInProgress) {
      // Começa o processo de renovação do accessToken
      this.refreshTokenInProgress = true;

      obs =
        this.authService.renewCredentials()
          .pipe(
            switchMap((token: TokenLogin) => {
              this.authService.storeCredentials(token);
              this.refreshTokenInProgress = false;
              this.refreshTokenSubject.next(token.accessToken);
              return next.handle(this.addNewAuthenticationToken(request));
            }),
            catchError((err: HttpErrorResponse, caught) => {
              const info = err.error || {};

              this.refreshTokenInProgress = false;
              this.removeRequest(request);
              const error = {
                statusCode: err.status || 401,
                descricao: info.descricao || info.Descricao || 'Credenciais expiradas ou serviço não disponível no momento',
                detalhe: info.detalhe || info.Detalhe,
                identificador: info.identificador || info.Identificador,
                titulo: info.titulo || info.Titulo || 'Erro!'
              } as AppResponseError;

              if (error.statusCode == 401) {
                this.refreshTokenSubject.next(null);
                this.authService.clearStorage();
                this.router.navigate(['/', 'logon']);
              }

              return throwError(error);
            })
          );



    } else {
      // Se já temos um refresh token em andamento, 
      // esperamos terminar e repetimos o último request.
      obs = this.refreshTokenSubject.pipe(
        take(1),
        switchMap((newAccestoken: string) => {
          return next.handle(this.addNewAuthenticationToken(request));
        }),
        catchError((err: HttpErrorResponse, caught) => {
          /*
          * Esse código atual tem como premissa que todos os requests esperando uma renovação
          *  conseguirão completar após uma renovação de token, sem que haja a necessidade de uma nova renovação.
          * Então temos que garantir que o tempo de expiração do accessToken seja grande o suficiente para acomodar todos os requests na fila.
          */

          const info = err.error || {};
          this.removeRequest(request);
          const error = {
            statusCode: err.status || 401,
            descricao: info.descricao || info.Descricao || 'Credenciais expiradas ou serviço não disponível no momento',
            detalhe: info.detalhe || info.Detalhe,
            identificador: info.identificador || info.Identificador,
            titulo: info.titulo || info.Titulo || 'Erro!'
          } as AppResponseError;
          return throwError(error);
        })
      );
    }
    return obs;
  }


  /**
   * 
   * @param request 
   */
  private addNewAuthenticationToken(request: HttpRequest<any>): HttpRequest<any> {

    const accessToken = this.authService.token;

    // If access token is null this means that user is not logged in
    // And we return the original request
    if (!accessToken) {
      return request;
    }

    // We clone the request, because the original request is immutable
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${accessToken}`
      }
    });
  }
}
